import React, { Fragment } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Link, Skeleton, Typography } from '@mui/material'

import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { colors } from '../../../../../themes/theme'
import { EventLevelTooltipContent, HierarchyLevel } from '../../EventLevelTooltipContent/EventLevelTooltipContent'

export interface EventHierarchyPathSegment {
  key: HierarchyLevel
  label: string
  // Unset when the event cannot name this level; the slot shows a dash.
  value?: string
  // Analytics page the value leads to, opened in a new tab. Unset = plain text.
  to?: string
  // The level exists but has no value yet (a blueprint no analyst has mapped to
  // a family): muted, with `note` explaining the gap on hover.
  isPlaceholder?: boolean
  note?: string
  // Drawn after the value, e.g. a discontinued marker.
  adornment?: React.ReactNode
}

interface EventHierarchyPathProps {
  segments: EventHierarchyPathSegment[]
  // The category colour, drawn as a small dot in front of the category name so
  // the path carries the same colour key as the tracker's event bars.
  accentColor?: string
  isLoading?: boolean
}

// Values stay on one line; a segment that does not fit moves down whole.
const valueSx = { fontSize: '0.75rem', lineHeight: 1.3, whiteSpace: 'nowrap' } as const

const SegmentLabel: React.FC<{ segment: EventHierarchyPathSegment }> = ({ segment }) => {
  return (
    <GRTooltip
      content={<EventLevelTooltipContent level={segment.key} showLevelDetails />}
      styles={{ cursor: 'help', display: 'inline-block', alignSelf: 'flex-start', lineHeight: 1.3 }}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{ fontSize: '9px', lineHeight: 1.3, textTransform: 'uppercase', letterSpacing: '0.7px', color: colors.textMuted, whiteSpace: 'nowrap' }}
      >
        {segment.label}
      </Typography>
    </GRTooltip>
  )
}

const SegmentValue: React.FC<{ segment: EventHierarchyPathSegment }> = ({ segment }) => {
  if (!segment.value) {
    return (
      <Typography component="span" sx={{ ...valueSx, color: 'text.disabled' }} aria-hidden>
        {'\u2014'}
      </Typography>
    )
  }
  if (segment.isPlaceholder) {
    const text = (
      <Typography component="span" sx={{ ...valueSx, color: 'text.disabled' }}>
        {segment.value}
      </Typography>
    )
    return segment.note ? (
      <GRTooltip content={segment.note} styles={{ cursor: 'help', display: 'inline-block' }}>
        {text}
      </GRTooltip>
    ) : (
      text
    )
  }
  if (segment.to) {
    return (
      <Link component={RouterLink} to={segment.to} target="_blank" rel="noopener noreferrer" underline="hover" sx={valueSx}>
        {segment.value}
      </Link>
    )
  }
  // The current level (the blueprint the dialog is about): ink, not a link.
  return (
    <Typography component="span" sx={{ ...valueSx, color: 'text.primary', fontWeight: 700 }}>
      {segment.value}
    </Typography>
  )
}

/**
 * Where the open event sits in the taxonomy — Category · Type · Blueprint
 * family · Blueprint — as the third line of the dialog header, under the
 * event title. Each level is a two-line stack (small-caps label over its
 * value); the stacks are parted by rules that run their full height rather
 * than by chevrons, so the line reads as labelled context, not a breadcrumb
 * trail. Wraps to a second line rather than truncating.
 */
export const EventHierarchyPath: React.FC<EventHierarchyPathProps> = ({ segments, accentColor, isLoading = false }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      flexWrap: 'wrap',
      columnGap: '14px',
      rowGap: 1,
      // Inside `DialogTitle`, which types itself as an h6 (20px / 32px line).
      // Reset so the stacks measure at their own size.
      fontSize: '0.75rem',
      lineHeight: 1.3,
    }}
  >
    {segments.map((segment, index) => (
      <Fragment key={segment.key}>
        {index > 0 && <Box aria-hidden sx={{ alignSelf: 'stretch', width: '1px', bgcolor: colors.ruleSubtle }} />}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <SegmentLabel segment={segment} />
          {isLoading ? (
            <Skeleton variant="text" width={110} sx={{ fontSize: '0.75rem' }} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* The category colour sits in front of the category name, on
                  its line, so the dot keys the value it belongs to. */}
              {index === 0 && accentColor && (
                <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: accentColor, flexShrink: 0, mr: 0.25 }} />
              )}
              <SegmentValue segment={segment} />
              {segment.adornment}
            </Box>
          )}
        </Box>
      </Fragment>
    ))}
  </Box>
)
